import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import { LegendItem, LegendItemProps } from "./useLegendItems"
import { isLegendItemInPDFSnapshot } from "./module"
import {
    selectColorTheme,
    selectLegendPosition,
    selectPDFLegendSize,
    selectShowTextFeatures,
} from "plugins/PDFv2/PDFSlice"
import { useViewport } from "features/map/GlobalViewState"
import { selectVisibleTexts } from "features/entity-caching/texts/selectors"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"

export const useTextLegendItems = ({ offsetX, offsetY, width, height }: LegendItemProps) => {
    const texts = useAppSelector(selectVisibleTexts)
    const legendSize = useAppSelector(selectPDFLegendSize())
    const legendPosition = useAppSelector(selectLegendPosition())
    const colorTheme = useAppSelector(selectColorTheme())
    const textFeaturesActive = useAppSelector(selectShowTextFeatures())

    const view = useViewport()

    const textLegendItems = useMemo(() => {
        const textsSet = new Set<string>()
        const items: LegendItem[] = []
        texts.forEach((text) => {
            const setKey = text.text
            if (textsSet.has(setKey)) return
            const [x, y] = view.project([text.longitude, text.latitude])
            const isInsideSnapshot = isLegendItemInPDFSnapshot(
                x,
                y,
                {
                    offsetX,
                    offsetY,
                    width,
                    height,
                },
                legendSize,
                legendPosition
            )
            if (isInsideSnapshot) {
                items.push({
                    icon: (
                        <Icons.ExtraSmallIcon
                            Icon={Icons.TextIcon}
                            iconClassName={classNames({
                                "text-white": colorTheme === "dark",
                                "text-gray-900": colorTheme === "light",
                            })}
                        />
                    ),
                    title: text.text,
                    id: text.id,
                })
                textsSet.add(setKey)
            }
        })
        return items
    }, [texts, view, offsetX, offsetY, width, height, legendSize, legendPosition, colorTheme])

    if (!textFeaturesActive) return []
    return textLegendItems
}
